import { format, utcToZonedTime } from 'date-fns-tz';
import {
  DEFAULT_TIMEZONE,
  composeCTDateTime,
  extractWallClockTime,
  getCTOffset as sharedGetCTOffset,
} from '@bodyanalytics/data-models-ui';

// #2091 — the shared meridiem-aware wall-clock parser (landed here via #2094)
// moved to `@bodyanalytics/data-models-ui` (ct-time.util.ts) so it lives beside
// the CT composition helpers and is importable by BOTH Nx graphs. Re-exported
// unchanged so existing `@bodyanalytics/ui-core` imports keep working.
export { extractWallClockTime };

export interface EventDateAndTimeI {
  testEventDate: string;
  startTime: string;
  endTime: string;
}

interface EventDateAndTimeCTI {
  testEventDateCT: string;
  startTimeCT: string;
  endTimeCT: string;
}

export class Helper {
  timezome: string;

  constructor() {
    this.timezome = DEFAULT_TIMEZONE;
  }

  getCTTime(dateTime: EventDateAndTimeI): EventDateAndTimeCTI {
    const { testEventDate, startTime, endTime } = dateTime;

    // #2091 — delegate the CT composition to the shared helper, anchoring the
    // dynamic CDT/CST offset to the selected date exactly as before.
    const selectedDate = new Date(testEventDate);
    const startTimeCT = composeCTDateTime(testEventDate, startTime, selectedDate); // 2025-03-28T10:00:00-05:00 (CT)
    const endTimeCT = composeCTDateTime(testEventDate, endTime, selectedDate); // 2025-03-28T11:30:00-05:00 (CT)

    // Format the date and time in ISO format (CT)
    const testEventDateCT = new Date(startTimeCT).toISOString(); // 2025-03-28T10:00:00.000Z (CT)

    return {
      testEventDateCT,
      startTimeCT,
      endTimeCT,
    };
  }

  // #2091 — delegates to the shared CT-offset helper (ct-time.util.ts); kept
  // as a method because call sites hold a `Helper` instance.
  getCTOffset(date: Date): string {
    return sharedGetCTOffset(date);
  }

  /**
   * #2088 — must never throw. Called per-row inside `ManageEventsTableComponent`'s
   * `@for` loop (via `timeRange()`); an uncaught error here aborts Angular's render
   * of the WHOLE table, not just the offending cell (repro: an operator-created
   * event persisted a raw "9:00 AM" string — `format()` throws `RangeError:
   * Invalid time value` on it — and all 11 rows vanished, not just the bad one).
   * Safe-parse instead: on anything unparseable, degrade to the raw stored value
   * (still informative — e.g. "9:00 AM" — for anyone trying to spot the bad row)
   * or '—' when there's nothing to show at all.
   */
  formatCTTime(time: string): string {
    if (!time) return '—';
    try {
      // Convert the test event date to Central Time (CT)
      const eventDateCT = utcToZonedTime(time, this.timezome);

      // Format the date for display
      return format(eventDateCT, 'h:mm a');
    } catch {
      return String(time);
    }
  }

  /**
   * Resolve the effective end moment of a testing event from its date + end time.
   * Mirrors the admin events-table "CLOSED" logic (#494): combine the calendar day
   * (`testEventDate`) with the wall-clock end time (`endTime`, HH:MM), falling back to
   * end-of-day when no time is present. Anchored to Central Time (the business timezone,
   * DEFAULT_TIMEZONE) via the dynamic CST/CDT offset — exactly like getCTTime() above —
   * so the past/upcoming decision is identical on any client timezone. Returns null when
   * the date is missing/unparseable.
   */
  eventEndDateTime(event: PastableEventI): Date | null {
    if (!event?.testEventDate) return null;
    const dateOnly = String(event.testEventDate).slice(0, 10);
    // Dynamic CT offset for the date ('-05:00' CDT / '-06:00' CST) so the absolute
    // instant — and thus the past check — never depends on the admin's browser
    // timezone. #2091 — composition delegated to the shared helper; the
    // end-of-day fallback keeps its :59 seconds, which composeCTDateTime (a
    // strict `HH:mm` + ':00' template) cannot express.
    const anchor = new Date(dateOnly);
    const hhmm = this.extractHHMM(event.endTime);
    const iso = hhmm ? composeCTDateTime(dateOnly, hhmm, anchor) : `${dateOnly}T23:59:59${sharedGetCTOffset(anchor)}`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * True when the event's end moment is already in the past relative to `now`
   * (defaults to the current time). `now` is injectable for deterministic tests.
   */
  isPastTestingEvent(event: PastableEventI, now: Date = new Date()): boolean {
    const end = this.eventEndDateTime(event);
    return !!end && end.getTime() < now.getTime();
  }

  /**
   * #2078/#2079 review: delegates to the shared meridiem-aware
   * `extractWallClockTime` (ct-time.util.ts in `@bodyanalytics/data-models-ui`
   * since #2091) instead of a naive `\d{2}:\d{2}`
   * digit grab. Two behavior fixes fall out: a legacy `"9:00 PM"` end time now
   * resolves to 21:00 CT (previously null → end-of-day fallback masked it),
   * and a single-digit-hour `"9:00"` now parses (previously null for the same
   * reason). Already-24h `"HH:MM"` inputs return identically, so the ISO
   * reconstruction in eventEndDateTime() is unchanged for them.
   */
  private extractHHMM(time: unknown): string | null {
    return extractWallClockTime(time);
  }
}

/** Minimal shape needed to compute whether a testing event is in the past. */
export interface PastableEventI {
  testEventDate?: string | Date | null;
  endTime?: string | null;
}
